import { useMemo, useState } from 'react';
import { CheckCircle2, Eye, EyeOff, RefreshCw, RotateCcw } from 'lucide-react';
import { useUpcomingEvents } from '../hooks/useUpcomingEvents.js';
import { useSyncShopifyPayouts } from '../hooks/useSyncShopifyPayouts.js';
import { useEntityScope } from '../hooks/useEntityScope.js';
import { useMarkAsPaid, useUnmarkAsPaid } from '../hooks/useMarkAsPaid.js';
import EntityBadge from '../components/EntityBadge.jsx';
import { formatCurrency } from '../lib/format.js';

// ── Date helpers ──────────────────────────────────────────────────────────────

function toLocalISODate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const shortDateFmt = new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: 'short' });
const monthYearFmt = new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric' });
const timeFmt = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' });

function formatShortDate(isoDate) {
  // Parse YYYY-MM-DD as local date to avoid UTC-shift
  const [y, mo, d] = isoDate.split('-').map(Number);
  return shortDateFmt.format(new Date(y, mo - 1, d));
}

function formatMonthYear(yyyymm) {
  const [y, mo] = yyyymm.split('-').map(Number);
  const label = monthYearFmt.format(new Date(y, mo - 1, 1));
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function relativeTime(isoTimestamp) {
  if (!isoTimestamp) return null;
  const diffMs = Date.now() - new Date(isoTimestamp).getTime();
  const diffMin = Math.round(diffMs / 60_000);
  if (diffMin < 1) return 'à l\'instant';
  if (diffMin === 1) return 'il y a 1 min';
  if (diffMin < 60) return `il y a ${diffMin} min`;
  const diffH = Math.round(diffMin / 60);
  if (diffH === 1) return 'il y a 1 h';
  return `il y a ${diffH} h`;
}

// Clé stable par événement
function eventKey(e) {
  return `${e.source}-${e.source_id}-${e.date}`;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function MonthSeparator({ label }) {
  return (
    <div className="flex items-center gap-3 py-3 px-1" aria-hidden>
      <div className="h-px flex-1 bg-slate-200" />
      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
        {label}
      </span>
      <div className="h-px flex-1 bg-slate-200" />
    </div>
  );
}

function CategoryBadge({ name, flowType, faded }) {
  if (!name) return null;
  const colorMap = {
    expense: 'bg-red-100 text-red-700',
    income: 'bg-green-100 text-green-700',
    neutral: 'bg-slate-100 text-slate-600',
  };
  const cls = faded ? 'bg-slate-100 text-slate-400' : (colorMap[flowType] ?? colorMap.neutral);
  return (
    <span
      className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium truncate shrink-0 w-[110px] md:w-[140px]${faded ? ' opacity-60' : ''} ${cls}`}
    >
      {name}
    </span>
  );
}

function EventRowSkeleton() {
  return (
    <div className="flex items-center gap-2 px-2 py-1.5 animate-pulse">
      <div className="w-[44px] h-3.5 rounded bg-slate-200 shrink-0" />
      <div className="w-[110px] md:w-[140px] h-5 rounded bg-slate-200 shrink-0" />
      <div className="flex-1 h-3.5 rounded bg-slate-200 min-w-0" />
      <div className="w-[80px] md:w-[100px] h-3.5 rounded bg-slate-200 shrink-0" />
      <div className="w-5 h-3.5 rounded bg-slate-100 shrink-0" />
      <div className="w-[72px] md:w-[100px] h-3.5 rounded bg-slate-100 shrink-0" />
    </div>
  );
}

// Sources qui supportent le marquage manuel comme payé
const MARKABLE_SOURCES = new Set(['future_expense', 'recurrence', 'debt_schedule', 'client_invoice']);

function EventRow({ event, isHidden, onToggle, onMarkPaid, onUnmarkPaid, showEntityBadge }) {
  const isPaid = event.paid === true;
  const todayISO = toLocalISODate(new Date());
  const isOverdue = event.date < todayISO && !isPaid;
  // client_invoice : `paid` n'est qu'un indice de réconciliation auto (transaction Qonto
  // détectée) — la facture reste `status='open'` en base tant que markPaid n'a pas été
  // appelé explicitement. Le bouton reste donc actionnable même si isPaid=true, pour
  // permettre de confirmer/lier la transaction détectée (voir useMarkAsPaid).
  const canMark = event.source === 'client_invoice'
    ? true
    : !isPaid && MARKABLE_SOURCES.has(event.source);
  // Annulation possible pour les récurrences et échéances de prêt marquées manuellement
  const canUnmark = isPaid
    && (event.source === 'recurrence' || event.source === 'debt_schedule')
    && event.paid_via === 'manual_mark';

  const amountCls = [
    'text-right tabular-nums shrink-0 w-[80px] md:w-[100px] text-sm font-medium',
    isPaid || isHidden
      ? 'text-slate-400 line-through opacity-60'
      : event.flow_type === 'income'
      ? 'text-green-600'
      : 'text-red-600',
  ].join(' ');

  const labelCls = [
    'flex-1 truncate text-sm min-w-0',
    isPaid || isHidden ? 'text-slate-400 line-through' : 'text-slate-800',
  ].join(' ');

  const sign = event.flow_type === 'income' ? '+' : '';
  const formattedAmount = `${sign}${formatCurrency(event.amount_cents)}`;
  const shortDate = formatShortDate(event.date);

  return (
    <div
      className={[
        'flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 rounded-md border-b border-slate-100 last:border-0 transition-opacity',
        isHidden ? 'opacity-40' : '',
      ].join(' ')}
    >
      {/* 1. Date */}
      <span className="text-slate-500 text-[11px] tabular-nums w-[44px] shrink-0 leading-none">
        {shortDate}
      </span>

      {/* 1bis. Badge entité — visible uniquement en vue groupe */}
      {showEntityBadge && event.entity_slug && (
        <EntityBadge slug={event.entity_slug} name={event.entity_slug} className="shrink-0" />
      )}

      {/* 2. Badge catégorie — largeur fixe pour stabilité de layout */}
      <CategoryBadge name={event.category_name} flowType={event.flow_type} faded={isPaid || isHidden} />

      {/* 3. Label + badge "En retard" inline */}
      <span className={labelCls} title={event.label}>
        {event.label}
        {isOverdue && (
          <span className="ml-1.5 inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700 align-middle whitespace-nowrap">
            En retard
          </span>
        )}
      </span>

      {/* 4. Montant */}
      <span className={amountCls}>
        {formattedAmount}
      </span>

      {/* 5a. Bouton "Marquer payé" — sources markables uniquement */}
      {canMark && (
        <button
          onClick={() => onMarkPaid(event)}
          aria-label="Marquer comme payé"
          title="Marquer comme payé"
          className="p-1 rounded text-slate-300 hover:text-emerald-600 shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
        >
          <CheckCircle2 size={13} aria-hidden />
        </button>
      )}
      {/* Placeholder quand pas de bouton marquer (aligne avec les rows qui en ont) */}
      {!canMark && !isPaid && <span className="w-[21px] shrink-0" aria-hidden />}

      {/* 5b. Bouton masquer/afficher — visible sur les events non payés uniquement */}
      {!isPaid && (
        <button
          onClick={() => onToggle(event._id)}
          aria-label={isHidden ? 'Afficher dans la projection' : 'Masquer de la projection'}
          className="p-1 rounded text-slate-300 hover:text-slate-500 shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
        >
          {isHidden ? <EyeOff size={13} aria-hidden /> : <Eye size={13} aria-hidden />}
        </button>
      )}
      {/* Placeholder pour aligner la colonne balance quand les boutons sont absents (isPaid) */}
      {isPaid && !canUnmark && <span className="w-[42px] shrink-0" aria-hidden />}

      {/* 5c. Bouton "Annuler le marquage" — récurrences manual_mark uniquement */}
      {canUnmark && (
        <button
          onClick={() => onUnmarkPaid(event)}
          aria-label="Annuler le marquage manuel"
          title="Annuler le marquage manuel"
          className="p-1 rounded text-slate-300 hover:text-orange-500 shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400"
        >
          <RotateCcw size={13} aria-hidden />
        </button>
      )}

      {/* 6. Colonne droite : badge Payé, balance projetée, ou — si masqué */}
      {isPaid ? (
        <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded shrink-0 w-[72px] md:w-[100px] text-center">
          Paye
        </span>
      ) : isHidden ? (
        <span className="text-slate-400 text-[11px] tabular-nums shrink-0 w-[72px] md:w-[100px] text-right">
          —
        </span>
      ) : (
        <span className="text-slate-500 text-[11px] tabular-nums shrink-0 w-[72px] md:w-[100px] text-right">
          {formatCurrency(event._balance_after)}
        </span>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

// Helpers centralisés ici pour éviter la duplication dans EventRow
function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

export default function UpcomingPage() {
  // Toggle "voir les retards" : par défaut on n'affiche que les 30 prochains
  // jours. L'utilisateur peut activer une fenêtre étendue [-30j ; +30j] pour
  // voir aussi les dépenses passées non réconciliées.
  const [showOverdue, setShowOverdue] = useState(false);

  const today = new Date();
  const from = toLocalISODate(addDays(today, showOverdue ? -30 : 0));
  const to = toLocalISODate(addDays(today, 30));

  const { data, isLoading, error, refetch, isFetching } = useUpcomingEvents({ from, to });
  const syncShopify = useSyncShopifyPayouts();
  const { isGroup } = useEntityScope();
  const markAsPaid = useMarkAsPaid();
  const unmarkAsPaid = useUnmarkAsPaid();
  const isBusy = isFetching || syncShopify.isPending;

  const handleRefresh = async () => {
    // La sync Shopify peut échouer (429, credentials, réseau) sans bloquer le refetch.
    // Les events restent utiles (récurrences, dépenses futures, dette, provisions).
    try {
      await syncShopify.mutateAsync();
    } catch {
      // Silencieux — pas de toast intrusif.
    }
    await refetch();
  };

  // État des événements masqués de la projection
  const [hiddenIds, setHiddenIds] = useState(new Set());

  const toggleHidden = (id) => {
    setHiddenIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Recalcul de la balance projetée en excluant les événements masqués
  const eventsWithBalance = useMemo(() => {
    if (!data?.events?.length) return [];
    let running = data.starting_balance_cents ?? 0;
    return data.events.map(ev => {
      const id = eventKey(ev);
      const isHidden = hiddenIds.has(id);
      const isPaid = ev.paid === true;
      // Les événements payés et les masqués n'impactent pas la balance projetée.
      // amount_cents est déjà signé par le backend (négatif pour expense), pas besoin de réinverser.
      if (!isPaid && !isHidden) {
        running += ev.amount_cents;
      }
      return { ...ev, _id: id, _balance_after: running };
    });
  }, [data, hiddenIds]);

  // Group events by YYYY-MM depuis eventsWithBalance
  const groups = useMemo(() => {
    if (!eventsWithBalance.length) return [];
    const map = new Map();
    for (const event of eventsWithBalance) {
      const key = event.date.slice(0, 7);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(event);
    }
    return Array.from(map.entries()).map(([month, events]) => ({ month, events }));
  }, [eventsWithBalance]);

  const balanceCents = data?.starting_balance_cents ?? null;
  const balanceAt = data?.starting_balance_at ?? null;
  const relTime = relativeTime(balanceAt);
  const balanceDate = balanceAt
    ? `${toLocalISODate(new Date(balanceAt))} · ${timeFmt.format(new Date(balanceAt))}`
    : null;

  const hiddenCount = hiddenIds.size;

  return (
    <div className="max-w-3xl mx-auto space-y-0 pb-24 lg:pb-8">
      {/* ── Header ── */}
      <div className="px-4 pt-4 pb-3 md:px-0 md:pt-0 space-y-1">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h1 className="text-xl font-bold text-slate-900 leading-tight">À payer</h1>
            <p className="text-sm text-slate-500">
              {showOverdue ? '30 prochains jours · retards 30 j' : '30 prochains jours'}
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={() => setShowOverdue((v) => !v)}
              className={[
                'px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors',
                showOverdue
                  ? 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'
                  : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50',
              ].join(' ')}
              aria-pressed={showOverdue}
              title={showOverdue ? 'Masquer les retards' : 'Afficher les retards des 30 derniers jours'}
            >
              {showOverdue ? 'Retards : on' : 'Retards : off'}
            </button>
            <button
              onClick={handleRefresh}
              disabled={isBusy}
              className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors disabled:opacity-40"
              aria-label="Actualiser"
            >
              <RefreshCw size={16} className={isBusy ? 'animate-spin' : ''} aria-hidden />
            </button>
          </div>
        </div>

        {balanceCents !== null && (
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-2xl font-bold tabular-nums text-slate-900">
              {formatCurrency(balanceCents)}
            </span>
            {balanceDate && (
              <span className="text-xs text-slate-400">
                au {balanceDate}
                {relTime && <> · Mis à jour {relTime}</>}
              </span>
            )}
          </div>
        )}

        <div className="flex items-center justify-between flex-wrap gap-1">
          <p className="text-xs text-slate-500">
            Paye : déjà imputé sur Qonto, exclu du solde projeté.
          </p>
          {hiddenCount > 0 && (
            <button
              onClick={() => setHiddenIds(new Set())}
              className="text-xs text-slate-400 hover:text-slate-600 underline underline-offset-2 shrink-0"
            >
              Réafficher tout ({hiddenCount})
            </button>
          )}
        </div>
      </div>

      {/* ── Contenu ── */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mx-0 md:mx-0">
        {isLoading && (
          <div>
            <EventRowSkeleton />
            <EventRowSkeleton />
            <EventRowSkeleton />
            <EventRowSkeleton />
          </div>
        )}

        {error && !isLoading && (
          <div className="px-4 py-8 text-center space-y-3">
            <p className="text-sm text-red-600">Impossible de charger les événements.</p>
            <button
              onClick={() => refetch()}
              className="text-sm font-medium text-slate-700 underline underline-offset-2 hover:text-slate-900"
            >
              Réessayer
            </button>
          </div>
        )}

        {!isLoading && !error && groups.length === 0 && (
          <div className="px-4 py-10 text-center">
            <p className="text-sm text-slate-500">Aucun événement prévu sur les 30 prochains jours.</p>
          </div>
        )}

        {!isLoading && !error && groups.length > 0 && (
          <div>
            {groups.map(({ month, events }) => (
              <div key={month}>
                <MonthSeparator label={formatMonthYear(month)} />
                {events.map((event) => (
                  <EventRow
                    key={event._id}
                    event={event}
                    isHidden={hiddenIds.has(event._id)}
                    onToggle={toggleHidden}
                    onMarkPaid={(ev) => markAsPaid.mutate(ev)}
                    onUnmarkPaid={(ev) => unmarkAsPaid.mutate(ev)}
                    showEntityBadge={isGroup}
                  />
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Footer hint ── */}
      {!isLoading && (
        <p className="px-4 pt-3 pb-1 text-xs text-slate-400 md:px-0">
          Inclut récurrences, dépenses futures, provisions, échéances de dette et factures clients.
        </p>
      )}
    </div>
  );
}
